import { Navigate, Outlet, Route, Routes } from "react-router-dom"
import { AlertTriangle } from "lucide-react"
import { AppShell } from "./components/layout/AppShell"
import { useApp } from "./state/AppState"
import { Button, Card } from "./components/ui"
import { BrandSpinner } from "./components/brand/Logo"
import { LandingPage } from "./pages/LandingPage"
import { SignInPage } from "./pages/auth/SignInPage"
import { RegisterPage } from "./pages/auth/RegisterPage"
import { ForgotPasswordPage } from "./pages/auth/ForgotPasswordPage"
import { CallbackPage } from "./pages/auth/CallbackPage"
import { DashboardPage } from "./pages/DashboardPage"
import { ProjectIssuesPage } from "./pages/ProjectIssuesPage"
import { IssueDetailPage } from "./pages/IssueDetailPage"
import { TagsPage } from "./pages/TagsPage"
import { ProjectTeamPage } from "./pages/ProjectTeamPage"
import { InsightsPage } from "./pages/InsightsPage"
import { TeamsPage } from "./pages/TeamsPage"
import { SettingsPage } from "./pages/settings/SettingsPage"
import { NotFoundPage } from "./pages/NotFoundPage"

export default function App() {
	const { status, error } = useApp()

	if (status === "loading") {
		return (
			<div className="grid min-h-screen place-items-center bg-ink-50">
				<BrandSpinner size={44} label="Starting Loomina" />
			</div>
		)
	}

	if (status === "error") return <BootError error={error} />

	return (
		<Routes>
			{/* Public */}
			<Route path="/" element={<Home />} />
			<Route path="/sign-in" element={<SignInPage />} />
			<Route path="/register" element={<RegisterPage />} />
			<Route path="/forgot-password" element={<ForgotPasswordPage />} />
			<Route path="/auth/callback" element={<CallbackPage />} />

			{/* Signed in */}
			<Route element={<RequireAuth />}>
				<Route element={<AppShell />}>
					<Route path="/app" element={<DashboardPage />} />
					<Route path="/projects/:projectId" element={<ProjectIssuesPage />} />
					<Route path="/projects/:projectId/tags" element={<TagsPage />} />
					<Route path="/projects/:projectId/team" element={<ProjectTeamPage />} />
					<Route path="/projects/:projectId/insights" element={<InsightsPage />} />
					<Route
						path="/projects/:projectId/issues/:issueId"
						element={<IssueDetailPage />}
					/>
					<Route path="/teams" element={<TeamsPage />} />
					<Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
					<Route path="/settings/:section" element={<SettingsPage />} />
					<Route path="/dashboard" element={<Navigate to="/app" replace />} />
					<Route path="*" element={<NotFoundPage />} />
				</Route>
			</Route>
		</Routes>
	)
}

/**
 * The front door.
 *
 * Someone already signed in has no use for the marketing page - they followed
 * a bookmark, or typed the bare domain, and what they want is their work.
 * Sending them straight through is the difference between the app and a
 * brochure they have to click past every time.
 */
function Home() {
	const { isSignedIn } = useApp()
	return isSignedIn ? <Navigate to="/app" replace /> : <LandingPage />
}

/**
 * The gate.
 *
 * A signed-out visitor is sent to the front page. It used to carry the path
 * they were heading for and drop them back on it after signing in, but a link
 * to a project that has since been deleted - or that this account was never
 * on - then left them staring at a dead page straight after signing up.
 * Everyone now arrives at their dashboard, where the projects they can reach
 * are listed.
 */
function RequireAuth() {
	const { isSignedIn } = useApp()

	if (isSignedIn) {
		// Rendered by the nested <Route element={<AppShell />}>.
		return <Outlet />
	}

	return <Navigate to="/" replace />
}

function BootError({ error }) {
	const offline = error?.code === "offline"
	return (
		<div className="grid min-h-screen place-items-center bg-ink-50 p-6">
			<Card className="max-w-lg p-6">
				<div className="flex items-start gap-3">
					<span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-rose-50 text-rose-600">
						<AlertTriangle size={17} aria-hidden />
					</span>
					<div>
						<h1 className="text-base font-semibold text-ink-900">
							{offline ? "Cannot reach the Loomina API" : "Something went wrong"}
						</h1>
						<p className="mt-1.5 text-[0.8125rem] leading-relaxed text-ink-600">
							{offline
								? "Start the local API, then reload this page."
								: error?.message || "An unexpected error occurred."}
						</p>
						{offline && (
							<pre className="mt-3 overflow-x-auto rounded-lg bg-ink-950 px-3 py-2.5 text-2xs leading-relaxed text-brand-200">
								cd "Loomina Backend"{"\n"}python src/local_server.py
							</pre>
						)}
						<Button
							className="mt-4"
							variant="primary"
							onClick={() => window.location.reload()}
						>
							Reload
						</Button>
					</div>
				</div>
			</Card>
		</div>
	)
}
